import { updateBusinessNameConverter } from 'data/converters/landlord.converter';
import {
  customSharedFromDb,
  customSharedToDb,
  customSharedUpdateToDb,
  defaultPermissionToDb,
  assignPermissionsToDb,
  userPermissionsFromDb,
  capabilitiesToDb,
  notificationsToDb,
} from 'data/converters/permissionSets.converters';
import { TUpdateBusinessNameUI } from 'data/types/landlord.types';
import {
  EPermissionType,
  TPMPermissionsDB,
  TCustomSharedPermissionSetUI,
  TUserPermissionsUI,
  TNotificationKey,
} from 'data/types/permissions.types';
import { EPropertyManagementUrl } from 'data/urls/propertyManagement.url';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { phoneNumberConverter, selectOptionConverter } from 'data/converters/general.converters';
import { TOptionTypeUi } from 'data/types/general.types';
import { TOptionsType } from 'components/Select';
import { downloadFile } from 'helpers/fileServices';
import { TPaymentConfigurationResponse } from 'data/types/propertyManagement.types';
import { ENotificationTypesDB, TAnimalUpdateConfig } from 'data/types/property.types';
import { authSplitApi } from '../helpers/slice.helpers';

const { PROPERTY_MANAGER } = EPropertyMangerUrl;
export const propertyManagementApi = authSplitApi('propertyManagementApi', [
  'pmPositions',
  'permissionSets',
  'defaultPermission',
]).injectEndpoints({
  endpoints: (build) => ({
    updateBusinessName: build.mutation<void, TUpdateBusinessNameUI>({
      query(data) {
        return {
          url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGEMENT}/${EPropertyManagementUrl.NAME}`,
          method: 'PATCH',
          body: updateBusinessNameConverter.toDb(data),
        };
      },
    }),
    getPropertyManagerPositions: build.query<TOptionsType, void>({
      query() {
        return {
          url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGERS}/${EPropertyManagementUrl.POSITION}`,
          method: 'GET',
        };
      },
      providesTags: ['pmPositions'],
      transformResponse: (data: TOptionsType) => (data || []).map((pm) => selectOptionConverter.fromDb(pm)),
    }),
    petChargingInfoExport: build.query<string, TOptionTypeUi[]>({
      query(propertyIds) {
        return {
          url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGEMENT}/${EPropertyManagementUrl.PET_CHARGING_INFO_PET}/${EPropertyManagementUrl.EXPORT}`,
          method: 'GET',
          params: { propertyIds: propertyIds.map((item) => item.value) },
          cache: 'no-cache',
          responseHandler: async (response) => {
            const file = await response.blob();
            downloadFile(file, 'No_Charged_Pets_Report', 'xls');
            return null;
          },
        };
      },
    }),
    updatePropertyManagerProfilePicture: build.mutation<void, { profilePicture: string }>({
      query(body) {
        return {
          url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGERS}/${EPropertyManagementUrl.PROFILE_PICTURE}`,
          method: 'PATCH',
          body,
        };
      },
    }),
    updatePropertyManagerPhone: build.mutation<void, { phone: string; propertyManagerId: string }>({
      query({ phone, propertyManagerId }) {
        return {
          url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGERS}/${EPropertyManagementUrl.PHONE}`,
          method: 'PATCH',
          body: { propertyManagerId, phone: phoneNumberConverter.toDb(phone) },
        };
      },
    }),
    updatePropertyManagerName: build.mutation<void, { name: string; propertyManagerId: string }>({
      query(body) {
        return {
          url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGERS}/${EPropertyManagementUrl.NAME}`,
          method: 'PATCH',
          body,
        };
      },
    }),
    updatePropertyManagerPosition: build.mutation<
      void,
      { position: { id?: string; value?: string }; propertyManagerId: string }
    >({
      query(body) {
        return {
          url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGERS}/${EPropertyManagementUrl.POSITION}`,
          method: 'PATCH',
          body,
        };
      },
    }),
    updateAutoScreeningDays: build.mutation<void, { days: number | null; propertyManagementId: string }>({
      query(body) {
        return {
          url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGEMENT}/${EPropertyManagementUrl.AUTO_SCREENING_DAYS}`,
          method: 'PATCH',
          body,
        };
      },
    }),
    getPaymentConfiguration: build.query<TPaymentConfigurationResponse, string>({
      query: (propertyManagementId) => ({
        url: `${PROPERTY_MANAGER}/${EPropertyManagementUrl.PROPERTY_MANAGEMENT}/${propertyManagementId}/${EPropertyManagementUrl.PAYMENT_CONFIGURATION}`,
        method: 'GET',
      }),
    }),
    // GET /permissions — without param: returns CUSTOM_SHARED sets only
    // GET /permissions?propertyManagerId=X — returns CUSTOM_SHARED sets + CUSTOM_INDIVIDUAL for that TM
    getPermissionSets: build.query<
      { sets: TCustomSharedPermissionSetUI[]; individual?: TUserPermissionsUI },
      string | void
    >({
      query(propertyManagerId) {
        return {
          url: `${PROPERTY_MANAGER}/permissions`,
          method: 'GET',
          ...(propertyManagerId && { params: { propertyManagerId } }),
        };
      },
      transformResponse: (data: TPMPermissionsDB[]) => {
        const items = data || [];
        const individualItem = items.find((p) => p.type === EPermissionType.CUSTOM_INDIVIDUAL);
        const sharedItems = items.filter((p) => p.type === EPermissionType.CUSTOM_SHARED);
        return {
          sets: sharedItems.map(customSharedFromDb),
          ...(individualItem && { individual: userPermissionsFromDb(individualItem) }),
        };
      },
      providesTags: ['permissionSets'],
    }),
    createPermissionSet: build.mutation<void, Omit<TCustomSharedPermissionSetUI, 'id' | 'linkedMembers'>>({
      query(data) {
        return {
          url: `${PROPERTY_MANAGER}/permissions`,
          method: 'POST',
          body: customSharedToDb(data),
        };
      },
      invalidatesTags: ['permissionSets', 'pmPositions'],
    }),
    updateCustomSharedPermissionSet: build.mutation<void, TCustomSharedPermissionSetUI>({
      query(data) {
        return {
          url: `${PROPERTY_MANAGER}/permissions`,
          method: 'PUT',
          body: { permissionsDTO: customSharedUpdateToDb(data) },
        };
      },
      invalidatesTags: ['permissionSets', 'pmPositions'],
    }),
    deleteCustomSharedPermissionSet: build.mutation<void, { id: string; reassignToSetId?: string }>({
      query({ id, reassignToSetId }) {
        return {
          url: `${PROPERTY_MANAGER}/permissions`,
          method: 'DELETE',
          body: { permissionsId: id, replacingPermissionsId: reassignToSetId || null },
        };
      },
      invalidatesTags: ['permissionSets'],
    }),
    updateTmPermissions: build.mutation<void, { propertyManagerId: string; permissions: TUserPermissionsUI }>({
      query(args) {
        return {
          url: `${PROPERTY_MANAGER}/property-managers/permissions-set`,
          method: 'PATCH',
          body: assignPermissionsToDb(args),
        };
      },
      invalidatesTags: ['permissionSets'],
    }),
    bulkAssignPermissions: build.mutation<
      void,
      {
        propertyManagerIds: string[];
        permissions: TUserPermissionsUI;
        notifications: TNotificationKey[];
        animalUpdateConfig?: TAnimalUpdateConfig | null;
      }
    >({
      query({ propertyManagerIds, permissions, notifications, animalUpdateConfig }) {
        const { notificationTypes: notificationOptions, isPrimaryContact } = notificationsToDb(notifications);
        const isAnimalActivitySummaryEnabled = notificationOptions.includes(
          ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY
        );
        return {
          url: `${PROPERTY_MANAGER}/property-managers/permissions-set-bulk`,
          method: 'PATCH',
          body: {
            propertyManagerIds,
            type: permissions.type,
            ...('setId' in permissions && permissions.setId && { permissionsId: permissions.setId }),
            ...('capabilities' in permissions && { permissions: capabilitiesToDb(permissions.capabilities) }),
            notificationOptions,
            isPrimaryContact,
            animalUpdateConfig: isAnimalActivitySummaryEnabled ? animalUpdateConfig ?? null : null,
          },
        };
      },
      invalidatesTags: ['permissionSets'],
    }),
    // GET /permissions/default — returns PMPermissionsDTO shape with type field
    getDefaultPermission: build.query<TUserPermissionsUI | null, void>({
      query() {
        return { url: `${PROPERTY_MANAGER}/permissions/default`, method: 'GET' };
      },
      transformResponse: (data: TPMPermissionsDB | null) => (data?.type ? userPermissionsFromDb(data) : null),
      providesTags: ['defaultPermission'],
    }),
    setDefaultPermission: build.mutation<void, TUserPermissionsUI>({
      query(data) {
        return {
          url: `${PROPERTY_MANAGER}/permissions/default`,
          method: 'POST',
          body: defaultPermissionToDb(data),
        };
      },
      invalidatesTags: ['defaultPermission'],
    }),
  }),
});

export const {
  useUpdateBusinessNameMutation,
  useGetPropertyManagerPositionsQuery,
  useLazyPetChargingInfoExportQuery,
  useUpdatePropertyManagerProfilePictureMutation,
  useUpdatePropertyManagerNameMutation,
  useUpdatePropertyManagerPhoneMutation,
  useUpdatePropertyManagerPositionMutation,
  useUpdateAutoScreeningDaysMutation,
  useGetPaymentConfigurationQuery,
  useGetPermissionSetsQuery,
  useCreatePermissionSetMutation,
  useUpdateCustomSharedPermissionSetMutation,
  useDeleteCustomSharedPermissionSetMutation,
  useBulkAssignPermissionsMutation,
  useUpdateTmPermissionsMutation,
  useGetDefaultPermissionQuery,
  useSetDefaultPermissionMutation,
} = propertyManagementApi;
